import { useMemo } from "react";
import { schoolQualifiesOpenToAll } from "../utils/openCatchmentAccess";

const NEARBY_OUTSIDE_MAX = 20;
/** Soft interest radius for Possible catchment (straight-line). */
const POSSIBLE_CATCHMENT_MAX_MI = 8;
const POSSIBLE_CATCHMENT_MAX_KM = POSSIBLE_CATCHMENT_MAX_MI * 1.609344;

function toRad(deg) {
  return (deg * Math.PI) / 180;
}

export function distanceKm(lat1, lon1, lat2, lon2) {
  if (
    !Number.isFinite(lat1) ||
    !Number.isFinite(lon1) ||
    !Number.isFinite(lat2) ||
    !Number.isFinite(lon2)
  )
    return Infinity;

  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;

  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Sort schools nearest-first from home. Name is a stable tie-break only.
 * Used by buckets and again after search/favourites so order never drifts.
 */
export function sortSchoolsByHomeDistance(schools, homeLocation) {
  if (!Array.isArray(schools) || schools.length < 2) {
    return Array.isArray(schools) ? schools : [];
  }
  const homeLat = Number(homeLocation?.lat);
  const homeLon = Number(homeLocation?.lon);
  if (!Number.isFinite(homeLat) || !Number.isFinite(homeLon)) {
    return schools;
  }
  return [...schools]
    .map((s) => ({
      s,
      d: distanceKm(homeLat, homeLon, Number(s.lat), Number(s.lon)),
      n: String(s?.name || ""),
    }))
    .sort((a, b) => a.d - b.d || a.n.localeCompare(b.n))
    .map((x) => x.s);
}

function cat(s) {
  return String(s?.catchment_category || "").trim().toLowerCase();
}

function hasCatchmentFlag(s) {
  return !!s?.has_catchment;
}

/**
 * No published drawable catchment yet, but distance may still matter:
 * - has_catchment false (no catchment info), or
 * - distance-first / historic NOD pending rings (Both, no radius/polygon yet).
 */
export function qualifiesPossibleCatchmentSchool(s) {
  if (!s) return false;
  if (!hasCatchmentFlag(s)) return true;
  const ctype = String(s.catchment_type || "").trim().toLowerCase();
  if (ctype === "historic_nod" && !s.show_radius && !s.show_polygon) return true;
  // Fallback if catchment_type missing on older payloads
  if (cat(s) === "both" && !s.show_radius && !s.show_polygon) return true;
  return false;
}

/**
 * Four-bucket split for left panel / mobile cards, sorted by distance from home:
 * 1) In catchment (priority/designated geography + home inside; not open-only)
 * 2) Open To All — open-access schools (pure Open, or Both with stated Open seats)
 *    that are NOT already In catchment (one list only; priority bucket wins).
 * 3) Possible catchment — ≤ ~8 mi; no catchment info OR distance-first pending NOD rings
 * 4) Outside — has a definite catchment, home outside it, and not Possible
 *
 * ensureSchoolIds: map-selected schools that fall past the nearest-20 cap are
 * appended (still distance-sorted). Schools already in the capped list keep
 * their distance order — check/uncheck must not jump them to the top.
 *
 * uncapDistanceLists: when true (left-panel search active), do not truncate
 * Possible / Outside to the nearest 20 — search must see every school.
 */
export function useSchoolListBuckets({
  inCatchmentSchools,
  catchmentCheckBySchoolId,
  allSchools,
  schoolsNear,
  homeLocation,
  ensureSchoolIds,
  uncapDistanceLists = false,
}) {
  const homeLat = Number(homeLocation?.lat);
  const homeLon = Number(homeLocation?.lon);
  const ensureKey = Array.isArray(ensureSchoolIds)
    ? ensureSchoolIds.join(",")
    : "";
  const hasHome = Number.isFinite(homeLat) && Number.isFinite(homeLon);

  return useMemo(() => {
    const schools = Array.isArray(allSchools)
      ? allSchools
      : Array.isArray(schoolsNear)
      ? schoolsNear
      : Array.isArray(inCatchmentSchools)
      ? inCatchmentSchools
      : [];

    const safeCatchmentMap = catchmentCheckBySchoolId || {};
    const ensureIds = new Set(
      (Array.isArray(ensureSchoolIds) ? ensureSchoolIds : []).filter(
        (id) => id != null
      )
    );

    const withDist = (arr) =>
      sortSchoolsByHomeDistance(arr, { lat: homeLat, lon: homeLon });

    /**
     * Nearest `max` by distance. Selected schools already inside that window
     * stay put. Only selected schools truncated past `max` are added back,
     * then the combined set is re-sorted by distance (never pinned to top).
     */
    const takeNearest = (arr, max) => {
      const sorted = withDist(arr);
      if (!ensureIds.size) return sorted.slice(0, max);
      const capped = sorted.slice(0, max);
      const cappedIds = new Set(capped.map((s) => s.id));
      const extras = sorted.filter(
        (s) => ensureIds.has(s.id) && !cappedIds.has(s.id)
      );
      if (!extras.length) return capped;
      return withDist([...capped, ...extras]);
    };

    const withinPossibleMiles = (s) => {
      if (!hasHome) return true;
      const d = distanceKm(homeLat, homeLon, Number(s.lat), Number(s.lon));
      return d <= POSSIBLE_CATCHMENT_MAX_KM;
    };

    const inCatchmentRaw = schools.filter((s) => {
      if (!hasCatchmentFlag(s)) return false;
      const check = safeCatchmentMap?.[s.id];
      if (!check?.inCatchment) return false;
      return cat(s) !== "open";
    });
    const topItems = withDist(inCatchmentRaw);
    const topIds = new Set(topItems.map((s) => s.id));

    // Open To All: open-access schools not already In catchment (Both + inside priority
    // must appear once — e.g. Tonbridge Area, not Area + Trustee Open lists).
    const openRaw = schools.filter(
      (s) => schoolQualifiesOpenToAll(s) && !topIds.has(s.id)
    );
    const middleItems = withDist(openRaw);
    const middleIds = new Set(middleItems.map((s) => s.id));

    // Possible catchment: soft distance interest, not proven in/out
    const possibleRaw = schools.filter(
      (s) =>
        !topIds.has(s.id) &&
        !middleIds.has(s.id) &&
        qualifiesPossibleCatchmentSchool(s) &&
        withinPossibleMiles(s)
    );
    // When search is active, keep full Possible set in-bucket for matching;
    // distance interest still applies for the Possible label, but Outside
    // must include every remaining school (not only nearest 20).
    const nearbyItems = uncapDistanceLists
      ? withDist(possibleRaw)
      : takeNearest(possibleRaw, NEARBY_OUTSIDE_MAX);
    const nearbyIds = new Set(nearbyItems.map((s) => s.id));

    // Outside: definite catchment miss, or Possible-type beyond the ~8 mi band
    const outsideRaw = schools.filter(
      (s) =>
        hasCatchmentFlag(s) &&
        !topIds.has(s.id) &&
        !middleIds.has(s.id) &&
        !nearbyIds.has(s.id) &&
        !schoolQualifiesOpenToAll(s) &&
        !safeCatchmentMap?.[s.id]?.inCatchment
    );
    const bottomItems = uncapDistanceLists
      ? withDist(outsideRaw)
      : takeNearest(outsideRaw, NEARBY_OUTSIDE_MAX);

    return {
      topItems,
      middleItems,
      nearbyItems,
      bottomItems,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ensureKey is a stable serialization of ensureSchoolIds
  }, [
    allSchools,
    schoolsNear,
    inCatchmentSchools,
    catchmentCheckBySchoolId,
    homeLat,
    homeLon,
    hasHome,
    ensureKey,
    uncapDistanceLists,
  ]);
}
